import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tile } from '../../../shared/components/ui/primitives';
import { getInventoryValueSummary } from '../../services/api/implementation/impl';
import { formatINR } from '../../utils/inventoryValue';
import { inrLakhCrore } from '../../utils/inrLakhCrore';

/** The project's stock value summary; shared cache for every screen that shows it. */
export function useInventoryValueSummary(projectId) {
  return useQuery({
    queryKey: ['inventoryValueSummary', projectId ? Number(projectId) : projectId],
    queryFn: () => getInventoryValueSummary(projectId),
    enabled: !!projectId,
    retry: false,
    staleTime: 60 * 1000
  });
}

/**
 * The figures to show: one warehouse's when `warehouseId` is set, else the
 * project's totals. Returns null while there is nothing to show.
 */
export function pickValueFigures(summary, warehouseId) {
  if (!summary) return null;
  if (warehouseId) {
    return (
      (summary.warehouses || []).find((w) => Number(w.warehouse_id) === Number(warehouseId)) ||
      null
    );
  }
  return summary.totals || null;
}

const lakhHint = (value) =>
  Math.abs(Number(value)) >= 100000 ? inrLakhCrore(value) : undefined;

/**
 * W4.1 / W4.3: Stock value, Own stock value, Lent out and Borrowed value, with a
 * quiet note for items that have stock but no rate yet (they count as ₹0).
 */
const StockValueStrip = ({ projectId, warehouseId = '', className = '' }) => {
  const { data, isError } = useInventoryValueSummary(projectId);
  const figures = pickValueFigures(data, warehouseId);

  // Older servers have no report route; the screen works as before without it.
  if (!projectId || isError) return null;

  const unpriced = Number(figures?.unpriced_items) || 0;
  const value = (key) => (figures ? formatINR(figures[key]) : '…');

  return (
    <div className={className}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Stock value"
          value={value('stock_value')}
          hint={lakhHint(figures?.stock_value) || 'Everything in the warehouse now'}
        />
        <Tile
          label="Own stock value"
          value={value('own_stock_value')}
          hint="Stock value + lent out − borrowed"
          tone="brand"
        />
        <Tile
          label="Lent out value"
          value={value('lent_out_value')}
          hint="Ours, at another project"
        />
        <Tile
          label="Borrowed value"
          value={value('borrowed_value')}
          hint="Here, but another project's"
        />
      </div>
      {unpriced > 0 && (
        <p className="mt-1.5 text-xs text-gray-500">
          {unpriced} item{unpriced === 1 ? '' : 's'} in stock without a rate, counted at ₹0. Add
          the rate from Stock entries to include {unpriced === 1 ? 'it' : 'them'}.
        </p>
      )}
    </div>
  );
};

export default StockValueStrip;
